import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const SESSION_TOKEN_KEY = "session_token";

/** Generate a unique session token */
export function generateSessionToken(): string {
  return crypto.randomUUID();
}

/** Register session token after login */
export async function registerSession(): Promise<string> {
  const token = generateSessionToken();
  localStorage.setItem(SESSION_TOKEN_KEY, token);
  await supabase.rpc("register_session", { _token: token });
  return token;
}

/** Hook: continuously checks that the current session is still the active one */
export function useSessionGuard() {
  const { toast } = useToast();
  const hasLoggedOut = useRef(false);

  useEffect(() => {
    const token = localStorage.getItem(SESSION_TOKEN_KEY);
    if (!token) return;

    const check = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session || hasLoggedOut.current) return;

      // Check account expiry
      const { data: profile } = await supabase
        .from("profiles")
        .select("expires_at")
        .eq("user_id", session.user.id)
        .single();

      if (profile?.expires_at) {
        const expiresAt = new Date(profile.expires_at);
        if (expiresAt <= new Date() && !hasLoggedOut.current) {
          hasLoggedOut.current = true;
          toast({
            title: "انتهت صلاحية الحساب",
            description: "انتهت مدة صلاحية حسابك. تواصل مع المدير لتجديد الصلاحية.",
            variant: "destructive",
          });
          localStorage.clear();
          await supabase.auth.signOut();
          window.location.href = "/auth";
          return;
        }
      }

      const { data: isValid } = await supabase.rpc("check_session", { _token: token });

      if (isValid === false && !hasLoggedOut.current) {
        hasLoggedOut.current = true;
        toast({
          title: "تم تسجيل الدخول من جهاز آخر",
          description: "تم إنهاء جلستك الحالية لأن حسابك مفتوح على جهاز آخر",
          variant: "destructive",
        });
        localStorage.clear();
        await supabase.auth.signOut();
        window.location.href = "/auth";
      }
    };

    // Check immediately
    check();

    // Then check every 30 seconds
    const interval = setInterval(check, 30_000);
    return () => clearInterval(interval);
  }, [toast]);
}
